import MarkdownIt from 'markdown-it';
import katex from '@traptitech/markdown-it-katex';
import hljs from 'highlight.js/lib/core';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github.css';

/**
 * highlight.js 언어 로더 — 14개 언어 정의를 메인 번들에서 빼고 사용 시점에
 * dynamic import한다. vite는 각 import를 별도 chunk로 분리하므로 사용자가
 * 마크다운에 특정 언어 코드블록을 쓸 때만 그 청크가 다운로드된다.
 */
type LangModule = { default: unknown };
const LANG_LOADERS: Record<string, () => Promise<LangModule>> = {
  javascript: () => import('highlight.js/lib/languages/javascript'),
  typescript: () => import('highlight.js/lib/languages/typescript'),
  python: () => import('highlight.js/lib/languages/python'),
  bash: () => import('highlight.js/lib/languages/bash'),
  json: () => import('highlight.js/lib/languages/json'),
  yaml: () => import('highlight.js/lib/languages/yaml'),
  markdown: () => import('highlight.js/lib/languages/markdown'),
  xml: () => import('highlight.js/lib/languages/xml'),
  css: () => import('highlight.js/lib/languages/css'),
  sql: () => import('highlight.js/lib/languages/sql'),
  java: () => import('highlight.js/lib/languages/java'),
  c: () => import('highlight.js/lib/languages/c'),
  cpp: () => import('highlight.js/lib/languages/cpp'),
  go: () => import('highlight.js/lib/languages/go'),
};

const LANG_ALIASES: Record<string, string> = {
  js: 'javascript',
  ts: 'typescript',
  py: 'python',
  sh: 'bash',
  yml: 'yaml',
  md: 'markdown',
  html: 'xml',
};

const langCache = new Map<string, Promise<void>>();
const RESOLVED: Promise<void> = Promise.resolve();

function canonicalLang(lang: string): string {
  return LANG_ALIASES[lang] ?? lang;
}

function ensureLanguage(lang: string): Promise<void> {
  const canon = canonicalLang(lang);
  const cached = langCache.get(canon);
  if (cached) return cached;
  // 이미 등록(주로 alias 등록 후) 또는 이번 세션에서 로드 완료 — 즉시 resolve.
  if (hljs.getLanguage(canon)) {
    langCache.set(canon, RESOLVED);
    return RESOLVED;
  }
  const loader = LANG_LOADERS[canon];
  if (!loader) {
    // 지원 안 되는 언어는 그냥 plain code로 떨어진다. 캐시해 재시도 막음.
    langCache.set(canon, RESOLVED);
    return RESOLVED;
  }
  const p = loader().then((mod) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hljs.registerLanguage(canon, mod.default as any);
    // 같은 정의를 별칭으로도 등록 — 사용자가 'js'로 입력해도 즉시 hit.
    for (const [alias, target] of Object.entries(LANG_ALIASES)) {
      if (target === canon) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hljs.registerLanguage(alias, mod.default as any);
      }
    }
  });
  langCache.set(canon, p);
  return p;
}

/**
 * 마크다운 입력에서 사용된 언어 fence를 추출. 코드블록이 없으면 빈 배열.
 * `mermaid`는 별도 lazy 경로(runMermaid)라 제외.
 */
const FENCE_RE = /^```([\w-]+)/gm;
export function extractLanguages(input: string): string[] {
  const langs = new Set<string>();
  let m: RegExpExecArray | null;
  FENCE_RE.lastIndex = 0;
  while ((m = FENCE_RE.exec(input)) !== null) {
    const lang = m[1];
    if (lang && lang !== 'mermaid') langs.add(lang);
  }
  return [...langs];
}

/**
 * 호출 전에 입력에 쓰인 언어 정의를 미리 로드한다. renderMarkdown은 동기라
 * 시점에 이미 등록돼 있어야 highlight가 먹는다.
 */
export async function ensureLanguages(langs: string[]): Promise<void> {
  await Promise.all(langs.map(ensureLanguage));
}

const md: MarkdownIt = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: false,
  highlight(code: string, lang: string): string {
    if (lang === 'mermaid') {
      return `<div class="mermaid-pending">${md.utils.escapeHtml(code)}</div>`;
    }
    if (lang && hljs.getLanguage(lang)) {
      try {
        const highlighted = hljs.highlight(code, { language: lang, ignoreIllegals: true }).value;
        return `<pre><code class="hljs language-${lang}">${highlighted}</code></pre>`;
      } catch {
        // fall through
      }
    }
    return `<pre><code>${md.utils.escapeHtml(code)}</code></pre>`;
  },
});

md.use(katex, { throwOnError: false, errorColor: '#b91c1c' });

/**
 * LaTeX/Pandoc 정통 표기 `\(...\)` / `\[...\]` 를 `$...$` / `$$...$$` 로 사전 치환.
 * @traptitech/markdown-it-katex는 dollar 표기만 인식하기 때문에, Pandoc/Quarto/
 * 학교 교과서 PDF 변환물처럼 brackets 표기를 쓰는 문서가 들어오면 수식이 plain
 * text로 떨어진다. 사용자가 표기를 의식하지 않게 양쪽 다 받기 위함.
 *
 * 코드 영역(fenced ``` ``` / inline `…`) 안의 `\(.../i` regex 같은 건 보호.
 */
const CODE_RE = /```[\s\S]*?```|`[^`\n]+`/g;
const INLINE_MATH_RE = /\\\(([\s\S]+?)\\\)/g;
const DISPLAY_MATH_RE = /\\\[([\s\S]+?)\\\]/g;
function normalizeLatexDelimiters(src: string): string {
  const placeholders: string[] = [];
  const protect = (m: string): string => {
    placeholders.push(m);
    return ` MDVIEW_CODE_${placeholders.length - 1} `;
  };
  let s = src.replace(CODE_RE, protect);
  s = s.replace(DISPLAY_MATH_RE, (_, body) => `$$${body}$$`);
  s = s.replace(INLINE_MATH_RE, (_, body) => `$${body}$`);
  return s.replace(/ MDVIEW_CODE_(\d+) /g, (_, i) => placeholders[Number(i)]);
}

export function renderMarkdown(input: string): string {
  return md.render(normalizeLatexDelimiters(input));
}

type MermaidTheme = 'neutral' | 'dark';

let mermaidLoaded: Promise<typeof import('mermaid').default> | null = null;
let currentMermaidTheme: MermaidTheme | null = null;

function detectMermaidTheme(): MermaidTheme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'neutral';
}

function loadMermaid(theme: MermaidTheme = detectMermaidTheme()): Promise<typeof import('mermaid').default> {
  if (!mermaidLoaded) {
    mermaidLoaded = import('mermaid').then((m) => {
      m.default.initialize({ startOnLoad: false, theme, securityLevel: 'strict' });
      currentMermaidTheme = theme;
      return m.default;
    });
    return mermaidLoaded;
  }
  // 이미 로드된 경우, 테마가 바뀌었으면 reinitialize — 다음 render 호출이 새 테마로 그림.
  return mermaidLoaded.then((m) => {
    if (currentMermaidTheme !== theme) {
      m.initialize({ startOnLoad: false, theme, securityLevel: 'strict' });
      currentMermaidTheme = theme;
    }
    return m;
  });
}

/**
 * Replace each `.mermaid-pending` element inside `container` with rendered SVG.
 * Failures per-block become inline error placeholders; the rest of the document survives.
 */
export async function runMermaid(container: HTMLElement): Promise<void> {
  const pending = container.querySelectorAll<HTMLElement>('.mermaid-pending');
  if (pending.length === 0) return;

  let mermaid: Awaited<ReturnType<typeof loadMermaid>>;
  try {
    mermaid = await loadMermaid();
  } catch {
    pending.forEach((el) => {
      el.outerHTML = '<div class="mermaid-error">다이어그램 라이브러리 로드 실패</div>';
    });
    return;
  }

  let i = 0;
  for (const el of pending) {
    const source = el.textContent ?? '';
    const id = `mermaid-${Date.now()}-${i++}`;
    try {
      const { svg } = await mermaid.render(id, source);
      const wrapper = document.createElement('div');
      wrapper.className = 'mermaid';
      wrapper.innerHTML = svg;
      el.replaceWith(wrapper);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '알 수 없는 오류';
      const errEl = document.createElement('div');
      errEl.className = 'mermaid-error';
      errEl.textContent = `다이어그램 렌더 실패: ${msg}`;
      el.replaceWith(errEl);
    }
  }
}
